import React from 'react';
import { observer } from 'mobx-react';
import { observable, computed} from 'mobx';
import styled from '@emotion/styled';
import { flexCenterColumn } from '@seedlang/style_mixins';
import Spinner from 'components/spinner';
import moment from 'moment';
import { each, keys, xor } from 'lodash';
import { AppUI } from '@seedlang/state';
import autobind from 'autobind-decorator';
import { Constants } from '@seedlang/constants';
import { isPresent, isBlank, kFormatter } from '@seedlang/utils';
import { LanguageIcon , FilterIcon } from '@seedlang/icons';
import AnalyticsDateModal from 'components/analytics/analytics_date_modal';
import AnalyticsFilterModal from 'components/analytics/analytics_filter_modal';
import AnalyticsDayAnalysis from 'components/analytics/analytics_day_analysis';
import AnalyticsList from 'components/analytics/analytics_list';
import Modal from 'components/modal';
import InPlaceSelect from 'components/form/in_place_select';

export const Header = styled.div`
  height: 50px;
  width: 100%;
  display: flex;
  align-items: center;
`;

export const Wrapper = styled.div`
  display: flex;
  align: self-start;
  select {
    height: 40px!important;
    border: 1px solid #FFF;
  }
`;

export const CalendarWrapper = styled.div`
  ${flexCenterColumn()}
  width: ${props => props.width};
`;

export const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-right: 5px;
  color: #333;
  width: 40px;
  height: 40px;
  &:hover {
    background: #e0e0e0;
  }
  border-radius: 50px;
  font-size: ${props => props.fontSize};
`;

export const DateString = styled.div`
  &:hover {
    background: ${props => props.hoverBackground};
  }
  cursor: ${props => props.cursor};
  font-size: 22px;
  font-weight: bold;
  padding: 10px 15px;
  border-radius: 40px;
`;

const LanguageIconWrapper = styled.div`
  display: flex;
  background: ${props => props.background};
  padding: 10px;
  border-radius: 5px;
  margin: 0 0 0 5px;
  cursor: pointer;
`;

const Count = styled.div`
  display: flex;
  font-weight: bold;
`;

const Stats = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex: 1;
`;

const languageIds = ['DE', 'FR', 'ES'];

@observer
class AnalyticsDefault extends React.Component {
  @observable loading = true;
  @observable currentMonth;
  @observable currentYear;
  @observable currentDay;
  @observable counts = {};
  @observable stats = {};
  @observable selectedDay;
  @observable selectedMonth;
  @observable selectedYear;
  @observable showDateModal = false;
  @observable activeLanguageIds;
  @observable timePeriod;
  @observable activeFields;
  @observable showFilterModal = false;
  @observable hiddenSections;

  constructor(props) {
    super(props);
    this.hiddenSections = this.getCookieValue('hidden-filter-sections') ? this.getCookieValue('hidden-filter-sections').split(',') : [];
    this.timePeriod = this.getCookieValue('timePeriod') || 'daily';
    this.activeLanguageIds = isPresent(this.getCookieValue('activeLanguageIds')) ? this.getCookieValue('activeLanguageIds').split(',') : languageIds;
    const todaysDate = new Date();
    this.currentMonth = parseInt(moment(todaysDate).format('M'), 10);
    this.currentYear = parseInt(moment(todaysDate).format('Y'), 10);
    this.currentDay = parseInt(moment(todaysDate).format('D'), 10);
    if (window.location.href.match('month')) { // for some reason props.params is not including month and date?
      const ary = window.location.href.split('/year/')[1].split('/month/');
      this.selectedMonth = parseInt(ary[1], 10);
      this.selectedYear = parseInt(ary[0], 10);
    } else {
      this.selectedMonth = this.currentMonth;
      this.selectedYear = this.currentYear;
    }
    (async () => {
      this.loading = true;
      this.activeFields = isPresent(this.getCookieValue('activeFields')) ? this.getCookieValue('activeFields').split(',') : keys(this.possibleFields);
      this.getAnalyticsIndex();
    })();
  }

  @computed get possibleFields() {
    const fields = {};

    if (!this.hiddenSections.includes('users')) {
      fields.newUsersCount = { name: 'New Users', tooltip: 'New users created on this date.', showBorder: true };
      fields.newWebUsersCount = { name: 'Web Users' };
      fields.newMobileUsersCount = { name: 'Mob Users' };
      fields.newPricing1UsersCount = { name: 'New1$ Users' };
      fields.newPricing2UsersCount = { name: 'New2$ Users' };
      fields.oldPricingUsersCount = { name: 'Old$ Users' };
    }

    if (!this.hiddenSections.includes('subscriptions')) {
      fields.newSubscriptionsCount = { name: 'All Subs', tooltip: 'New subscriptions created on this date.', showBorder: true};
      fields.newWebSubscriptionsCount = { name: 'Web Subs'};
      fields.newMobileSubscriptionsCount = { name: 'Mob Subs'};
      fields.cancellationsCount = { name: 'Canc', tooltip: 'New Cancellations' };
    }

    if (!this.hiddenSections.includes('new-subs')) {
      fields.newUsersSubscriptionsCount = { name: 'New Subs', tooltip: 'Subscriptions created by new users who joined on this date.', showBorder: true};
      fields.newUsersSubscriptionsConversionPercentage = { name: 'Cnv%', tooltip: 'Conversion percentage for users who joined on this date.', prefix: '%' };
      fields.newUsersSubscriptionsRevenue = { name: 'Revenue', tooltip: 'Revenue for users who joined on this date.', prefix: '$' };
      fields.newUsersSubscriptionsInitialRevPerUser = { name: 'Rev/New User', tooltip: 'Intial revenue per user for users who joined on this date.', prefix: '$' };
      fields.newUsersSubscriptionsAverageOrder = { name: 'Avg Order', tooltip: 'Average purchase order for users who joined on this date.', prefix: '$' };
      fields.newUsersSubscriptionsAverageMonths = { name: 'Avg Mnths', tooltip: 'Average months for users who joined on this date.' };
    }

    if (!this.hiddenSections.includes('default-price')) {
      fields.oldPricingSubscriptionsCount = { name: 'Old$', tooltip: 'Subscriptions Old Pricing', showBorder: true };
      fields.oldPricingConversionPercentage = { name: 'Cnv%', tooltip: 'Conversion Percentage by Old Pricing', prefix: '%' };
      fields.oldPricingRevenue = { name: 'Revenue', tooltip: 'Conversion Percentage By Old Pricing', prefix: '$' };
      fields.oldPricingInitialRevPerUser = { name: 'Rev/New User', tooltip: 'Intial revenue per user by old pricing.', prefix: '$' };
      fields.oldPricingAverageOrder = { name: 'Avg Order', tooltip: 'Average Purchase Order By Old Pricing', prefix: '$' };
      fields.oldPricingAverageMonths = { name: 'Avg Mnths', tooltip: 'Average Months By Old Pricing.' };
    }

    if (!this.hiddenSections.includes('updated-1-price')) {
      fields.newPricing1SubscriptionsCount = { name: 'New$ (1)', tooltip: 'Subscriptions New Pricing', showBorder: true };
      fields.newPricing1ConversionPercentage = { name: 'Cnv%', tooltip: 'Conversion Percentage by New Pricing', prefix: '%' };
      fields.newPricing1Revenue = { name: 'Revenue', tooltip: 'Conversion Percentage By New Pricing', prefix: '$' };
      fields.newPricing1InitialRevPerUser = { name: 'Rev/New User', tooltip: 'Intial revenue per user by new pricing.', prefix: '$' };
      fields.newPricing1AverageOrder = { name: 'Avg Order', tooltip: 'Average Purchase Order By New Pricing', prefix: '$' };
      fields.newPricing1AverageMonths = { name: 'Avg Mnths', tooltip: 'Average Months By New Pricing.' };
    }

    if (!this.hiddenSections.includes('updated-2-price')) {
      fields.newPricing2SubscriptionsCount = { name: 'New$ (2)', tooltip: 'Subscriptions New Pricing', showBorder: true };
      fields.newPricing2ConversionPercentage = { name: 'Cnv%', tooltip: 'Conversion Percentage by New Pricing', prefix: '%' };
      fields.newPricing2Revenue = { name: 'Revenue', tooltip: 'Conversion Percentage By New Pricing', prefix: '$' };
      fields.newPricing2InitialRevPerUser = { name: 'Rev/New User', tooltip: 'Intial revenue per user by new pricing.', prefix: '$' };
      fields.newPricing2AverageOrder = { name: 'Avg Order', tooltip: 'Average Purchase Order By New Pricing', prefix: '$' };
      fields.newPricing2AverageMonths = { name: 'Avg Mnths', tooltip: 'Average Months By New Pricing.' };
    }

    if (!this.hiddenSections.includes('web')) {
      fields.webSubscriptionsCount = { name: 'Web', tooltip: 'Subscriptions Web', showBorder: true };
      fields.webConversionPercentage = { name: 'Cnv%', tooltip: 'Conversion Percentage by Web', prefix: '%' };
      fields.webRevenue = { name: 'Revenue', tooltip: 'Conversion Percentage By Web', prefix: '$' };
      fields.webInitialRevPerUser = { name: 'Rev/New User', tooltip: 'Intial revenue per user by web.', prefix: '$' };
      fields.webAverageOrder = { name: 'Avg Order', tooltip: 'Average Purchase Order By Web', prefix: '$' };
      fields.webAverageMonths = { name: 'Avg Mnths', tooltip: 'Average Months By Web.' };
    }

    if (!this.hiddenSections.includes('mobile')) {
      fields.mobileSubscriptionsCount = { name: 'Mobile', tooltip: 'Subscriptions Mobile', showBorder: true };
      fields.mobileConversionPercentage = { name: 'Cnv%', tooltip: 'Conversion Percentage by Mobile', prefix: '%' };
      fields.mobileRevenue = { name: 'Revenue', tooltip: 'Conversion Percentage By Mobile', prefix: '$' };
      fields.mobileInitialRevPerUser = { name: 'Rev/New User', tooltip: 'Intial revenue per user by mobile.', prefix: '$' };
      fields.mobileAverageOrder = { name: 'Avg Order', tooltip: 'Average Purchase Order By Mobile', prefix: '$' };
      fields.mobileAverageMonths = { name: 'Avg Mnths', tooltip: 'Average Months By Mobile.' };
    }

    if (!this.hiddenSections.includes('ative-users')) {
      fields.dailyActiveUsersCount = { name: 'DAU', tooltip: 'Daily Active Users', showBorder: true };
      fields.weeklyActiveUsersCount = { name: 'WAU', tooltip: 'Weekly Active Users' };
      fields.monthlyActiveUsersCount = { name: 'MAU', tooltip: 'Monthly Active Users' };
    }

    if (!this.hiddenSections.includes('deck-attempts')) {
      fields.firstDeckAttemptedCount = { name: '1stD Att', tooltip: '1st Deck Attempted', showBorder: true };
      fields.firstDeckCompletedCount = { name: '1stD Compl', tooltip: '1st Deck Completed' };
      fields.firstDeckCompletedPercentage = { name: '1st Deck Compl%', tooltip: '1st Deck Completed Percentage' };
    }

    return fields;
  }

  @computed get orderedActiveLanguageIds() {
    return languageIds.filter(item => {
      return this.activeLanguageIds.includes(item);
    });
  }

  @computed get selectedDateString() {
    const selectedDayWithZero = String(this.selectedDay).padStart(2, '0');
    const selectedMonthWithZero = String(this.selectedMonth).padStart(2, '0');
    return `${this.selectedYear}-${selectedMonthWithZero}-${selectedDayWithZero}`;
  }

  @computed get fromDate() {
    const month = this.timePeriod === 'daily' ? this.selectedMonth - 1 : 0;
    return new Date(this.selectedYear, month, 1);
  }

  @computed get toDate() {
    const month = this.timePeriod === 'daily' ? this.selectedMonth : 11;
    return new Date(this.selectedYear, month, 31);
  }

  @computed get visibleFieldsWithDetails() {
    return keys(this.possibleFields).filter(fieldKey => this.activeFields.includes(fieldKey)).map(fieldKey => [fieldKey, this.possibleFields[fieldKey]]);
  }

  @autobind onUpdateHiddenSections(section) {
    if (this.hiddenSections.includes(section)) {
      this.hiddenSections = this.hiddenSections.filter(item => item !== section);
    } else {
      this.hiddenSections.push(section);
    }
    this.setCookieValue('hidden-filter-sections', this.hiddenSections.join(','));
  }

  @autobind afterGetAnalyticsIndex(resp) {
    this.loading = false;
    if (isBlank(resp) && !Array.isArray(resp)) { return; }
    this.counts = resp;
    each(languageIds, languageId => {
      this.stats[languageId] = {};
      each(['newUsersCount', 'newSubscriptionsCount'], type => {
        this.stats[languageId][type] = {max: 0, sum: 0, count: 0, average: 0};
        this.counts.filter(item => item.languageId === languageId).map(result => {
          this.stats[languageId][type]['count'] += 1;
          this.stats[languageId][type]['sum'] += result[type];
          this.stats[languageId][type]['average'] = parseInt(this.stats[languageId][type]['sum'] / this.stats[languageId][type]['count'], 10);
        });
      });
    });
  }

  @autobind changeDate(incrementOrDecrement) {
    const value = incrementOrDecrement === 'increment' ? 1 : -1;
    if (this.timePeriod === 'daily') {
      const date = new Date(this.selectedYear, this.selectedMonth - 1 + value, 1);
      this.selectedYear = date.getFullYear();
      this.selectedMonth = date.getMonth() + 1;
    } else {
      this.selectedYear = this.selectedYear + value;
    }
    AppUI.routeStore.routeToNamed('builder.dashboard.analytics_with_month', { month: this.selectedMonth, year: this.selectedYear});
    this.getAnalyticsIndex();
  }

  @autobind getAnalyticsIndex() {
    this.loading = true;
    AppUI.analyticStore.getIndex({queryStrings: {white_label: false}, filters: {
      time_period: this.timePeriod,
      date: `RANGE[${this.fromDate.toISOString()},${this.toDate.toISOString()}]`,
    }}, this.afterGetAnalyticsIndex);
  }

  @autobind onSelectDate(year, month) {
    this.selectedYear = year;
    this.selectedMonth = month;
    this.showDateModal = false;
    AppUI.routeStore.routeToNamed('builder.dashboard.analytics_with_month', {month: this.selectedMonth, year: this.selectedYear});
    this.getAnalyticsIndex();
  }

  @autobind onSelectDay(day) {
    this.selectedDay = day;
  }

  @autobind onCloseAnalysisModal() {
    this.selectedDay = null;
    AppUI.userSubscriptionStore.clearIndexData();
    AppUI.subscriptionStore.clearIndexData();
  }

  @autobind toggleActiveLanguageId(languageId) {
    this.activeLanguageIds = xor(this.activeLanguageIds, [languageId]);
    this.setCookieValue('activeLanguageIds', this.activeLanguageIds.join(','));
  }

  @autobind onSetTimePeriod(timePeriod) {
    this.timePeriod = timePeriod;
    this.setCookieValue('timePeriod', this.timePeriod);
    this.getAnalyticsIndex();
  }

  @autobind getCookieValue(field) {
    return AppUI.getCookieValue(`analytics-default-${field}`);
  }

  @autobind setCookieValue(field, value) {
    AppUI.setCookieValue(`analytics-default-${field}`, value);
  }

  render() {
    return (
      <Wrapper>
        {
          this.loading &&
            <Spinner />
        }
        {
          this.showDateModal &&
            <Modal
              width='400px'
              height='250px'
              hideCloseIcon
              onCloseModal={() => this.showDateModal = false}
            >
              <AnalyticsDateModal
                onSelectDate={this.onSelectDate}
                year={this.selectedYear}
              />
            </Modal>
        }
        {
          this.showFilterModal &&
            <Modal
              width='400px'
              height='450px'
              hideCloseIcon
              onCloseModal={() => this.showFilterModal = false}
            >
              <AnalyticsFilterModal
                sections={[
                  ['User Counts', 'users'],
                  ['Total Subscription Counts', 'subscriptions'],
                  ['Subscriptions of Users Created on Date', 'new-subs'],
                  ['Default Pricing Subscriptions', 'default-price', '1 month: 8.99, 3 month: 19.99, 1 year: 59.99'],
                  ['New Pricing (1) Subscriptions', 'updated-1-price', '1 month: 12.99, 1 year: 69.99, lifetime: 179.99'],
                  ['New Pricing (2) Subscriptions', 'updated-2-price', '1 month: 10.99, 3 month: 23.99, 1 year: 69.99'],
                  ['Web Subscriptions', 'web'],
                  ['Mobile Subscriptions', 'mobile'],
                  ['Active Users Counts', 'active-users'],
                  ['Deck Attempts', 'deck-attempts'],
                ]}
                hiddenSections={this.hiddenSections}
                onUpdateHiddenSections={this.onUpdateHiddenSections}
              />
            </Modal>
        }
        {
          this.selectedDay && isPresent(this.counts) &&
            <Modal
              width='800px'
              height='700px'
              onCloseModal={this.onCloseAnalysisModal}
              hideCloseIcon
            >
              <AnalyticsDayAnalysis
                languageIds={languageIds}
                activeLanguageIds={this.activeLanguageIds}
                dateString={this.selectedDateString}
                stats={this.stats}
                counts={this.counts}
                month={this.selectedMonth}
                year={this.selectedYear}
                day={this.selectedDay}
              />
            </Modal>
        }
        {
          !this.loading &&
            <CalendarWrapper width='1000px'>
              <Header>
                <IconWrapper
                  fontSize='28px'
                  onClick={() => this.changeDate('decrement')}
                >
                  <i className='fa fa-angle-left' />
                </IconWrapper>
                <IconWrapper
                  fontSize='28px'
                  onClick={() => this.changeDate('increment')}
                >
                  <i className='fa fa-angle-right' />
                </IconWrapper>
                <InPlaceSelect
                  margin='0 5px 0 0'
                  options={[['daily', 'Daily'], ['weekly', 'Weekly'], ['monthly', 'Monthly']]}
                  onChange={val => this.onSetTimePeriod(val)}
                  value={this.timePeriod}
                />
                {
                  this.timePeriod === 'daily' &&
                    <DateString
                      hoverBackground='#e0e0e0'
                      cursor='pointer'
                      onClick={() => this.showDateModal = true}
                    >
                      {Constants.MONTHS[this.selectedMonth - 1]} {this.selectedYear}
                    </DateString>
                }
                {
                  this.timePeriod !== 'daily' &&
                    <DateString
                      cursor='default'
                    >
                      {this.selectedYear}
                    </DateString>
                }
                <Stats>
                  <LanguageIconWrapper
                    background='#CCC'
                    onClick={() => this.showFilterModal = true}
                  >
                    <FilterIcon
                      width='20px'
                      height='20px'
                      fill='#444'
                    />
                  </LanguageIconWrapper>
                  {
                    languageIds.map(item => {
                      return (
                        <LanguageIconWrapper
                          key={item}
                          onClick={() => this.toggleActiveLanguageId(item)}
                          background={this.activeLanguageIds.includes(item) ? '#FFF' : '#CCC'}
                        >
                          <LanguageIcon
                            languageId={item}
                            width='20px'
                            height='20px'
                            margin='0 5px 0 0'
                          />
                          {
                            this.stats[item] && this.stats[item][this.statType] &&
                              <Count>
                                {kFormatter(this.stats[item][this.statType][this.currentStat])}
                              </Count>
                          }
                        </LanguageIconWrapper>
                      );
                    })
                  }
                </Stats>
              </Header>
              {
                isPresent(this.counts) &&
                  <AnalyticsList
                    counts={this.counts}
                    timePeriod={this.timePeriod}
                    selectedMonth={this.selectedMonth}
                    selectedYear={this.selectedYear}
                    languageIds={this.orderedActiveLanguageIds}
                    onSelectDay={this.onSelectDay}
                    statFields={this.visibleFieldsWithDetails}
                    allowClickHeader
                    dataColumnWidth={100}
                  />
                }
            </CalendarWrapper>
        }
      </Wrapper>
    );
  }
}

export default AnalyticsDefault;
